/**
 * VIN Pulse OS - Web API Service
 * Centralized API client for all HTTP requests
 * Handles authentication, token management, and error responses
 */

import axios, { AxiosInstance, AxiosError } from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

interface ApiError {
    code: string;
    message: string;
    details?: Record<string, any>;
}

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: ApiError;
    message?: string;
}

class ApiClient {
    private client: AxiosInstance;
    private accessToken: string | null = null;
    private refreshToken: string | null = null;
    private isRefreshing = false;
    private refreshSubscribers: Array<(token: string) => void> = [];

    constructor() {
        this.client = axios.create({
            baseURL: API_BASE_URL,
            timeout: 30000,
            headers: {
                "Content-Type": "application/json",
            },
        });

        // Load tokens from localStorage
        this.loadTokensFromStorage();

        // Add request interceptor for authentication
        this.client.interceptors.request.use(
            (config) => {
                if (this.accessToken) {
                    config.headers.Authorization = `Bearer ${this.accessToken}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Add response interceptor for token refresh
        this.client.interceptors.response.use(
            (response) => response,
            async (error: AxiosError) => {
                const originalRequest = error.config as any;

                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    if (this.isRefreshing) {
                        // Wait for token refresh to complete
                        return new Promise((resolve) => {
                            this.refreshSubscribers.push((token) => {
                                originalRequest.headers.Authorization = `Bearer ${token}`;
                                resolve(this.client(originalRequest));
                            });
                        });
                    }

                    this.isRefreshing = true;

                    try {
                        const newAccessToken = await this.refreshAccessToken();
                        this.isRefreshing = false;
                        this.onRefreshed(newAccessToken);

                        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                        return this.client(originalRequest);
                    } catch (refreshError) {
                        this.isRefreshing = false;
                        this.clearTokens();
                        throw refreshError;
                    }
                }

                return Promise.reject(error);
            }
        );
    }

    /**
     * Load tokens from localStorage
     */
    private loadTokensFromStorage(): void {
        try {
            const accessToken = localStorage.getItem("accessToken");
            const refreshToken = localStorage.getItem("refreshToken");
            if (accessToken) {
                this.accessToken = accessToken;
            }
            if (refreshToken) {
                this.refreshToken = refreshToken;
            }
        } catch (error) {
            console.error("Error loading tokens from storage:", error);
        }
    }

    /**
     * Login user with email and password
     */
    async login(
        email: string,
        password: string
    ): Promise<{
        accessToken: string;
        refreshToken: string;
        user: {
            id: string;
            email: string;
            role: string;
            full_name: string;
            must_change_password?: boolean;
            account_status?: 'active' | 'inactive' | 'suspended';
        };
    }> {
        try {
            const response = await this.client.post<any>("/login", {
                email,
                password,
            });

            // Handle both response formats:
            // Format 1: { success: true, data: { accessToken, refreshToken, user } }
            // Format 2: { accessToken, refreshToken, user }
            const data = response.data.data || response.data;

            if (data && data.accessToken && data.refreshToken) {
                const { accessToken, refreshToken } = data;
                this.saveTokens(accessToken, refreshToken);
                return {
                    accessToken,
                    refreshToken,
                    user: data.user || { id: '', email: '', role: '', full_name: '' }
                };
            }

            throw new Error(response.data.message || "Login failed - invalid response format");
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Refresh access token using refresh token
     */
    private async refreshAccessToken(): Promise<string> {
        try {
            if (!this.refreshToken) {
                throw new Error("No refresh token available");
            }

            const response = await axios.post<{
                accessToken: string;
                refreshToken: string;
                expiresIn: number;
            }>(`${API_BASE_URL}/token/refresh`, {
                refreshToken: this.refreshToken,
            });

            const data = (response.data as any).data || response.data;

            if (data && data.accessToken && data.refreshToken) {
                this.saveTokens(data.accessToken, data.refreshToken);
                return data.accessToken;
            }

            throw new Error("Token refresh failed");
        } catch (error) {
            this.clearTokens();
            throw error;
        }
    }

    /**
     * Save tokens to localStorage and memory
     */
    private saveTokens(accessToken: string, refreshToken: string): void {
        try {
            this.accessToken = accessToken;
            this.refreshToken = refreshToken;
            localStorage.setItem("accessToken", accessToken);
            localStorage.setItem("refreshToken", refreshToken);
        } catch (error) {
            console.error("Error saving tokens:", error);
            throw error;
        }
    }

    /**
     * Clear stored tokens
     */
    clearTokens(): void {
        this.accessToken = null;
        this.refreshToken = null;
        try {
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
        } catch (error) {
            console.error("Error clearing tokens:", error);
        }
    }

    /**
     * Called when token is refreshed
     */
    private onRefreshed(token: string): void {
        this.refreshSubscribers.forEach((callback) => callback(token));
        this.refreshSubscribers = [];
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        return !!this.accessToken;
    }

    /**
     * Get access token
     */
    getAccessToken(): string | null {
        return this.accessToken;
    }

    /**
     * Logout user
     */
    logout(): void {
        this.clearTokens();
    }

    /**
     * Get user endpoint - verify authentication
     */
    async getMe(): Promise<{
        id: string;
        email: string;
        role: string;
        full_name: string;
        is_active: boolean;
        account_status?: 'active' | 'inactive' | 'suspended';
        must_change_password?: boolean;
        phone?: string | null;
        employee_id?: string | null;
        department?: string | null;
        territory?: string | null;
        warehouse_code?: string | null;
        notes?: string | null;
    }> {
        try {
            const response = await this.client.get<any>("/users/me");
            const data = response.data?.data || response.data;
            if (data && data.id !== undefined) {
                return {
                    id: String(data.id),
                    email: data.email,
                    role: data.role,
                    full_name: data.full_name,
                    is_active: data.is_active ?? true,
                    account_status: data.account_status,
                    must_change_password: data.must_change_password === true,
                    phone: data.phone ?? null,
                    employee_id: data.employee_id ?? null,
                    department: data.department ?? null,
                    territory: data.territory ?? null,
                    warehouse_code: data.warehouse_code ?? null,
                    notes: data.notes ?? null,
                };
            }
            throw new Error(response.data?.message || "Failed to fetch user");
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Unwrap the HTTP response into the payload the caller expects.
     *
     * The backend uses two response styles:
     *   1. Explicit envelope:  `{ success: true, data: <payload> }`
     *      e.g. /customers/me/orders, /sales-reps/me/orders, DELETE /orders/:id
     *   2. Raw payload:        the payload object itself, which may legitimately
     *      contain a top-level `data` field as part of the data
     *      e.g. `/notifications` returns `{ data: [...], pagination: {...}, unread_count }`
     *           `/skus` returns `{ data: [...], pagination: {...} }`
     *           `/categories` returns `{ data: [...] }`
     *
     * We MUST only unwrap when style 1 is detected (presence of `success: true`),
     * otherwise calls to style 2 endpoints lose their pagination / metadata.
     * The previous implementation always unwrapped `.data`, which silently
     * dropped pagination on every list endpoint that used style 2.
     */
    private unwrapBody<T>(body: any): T {
        if (
            body &&
            typeof body === 'object' &&
            !Array.isArray(body) &&
            body.success === true &&
            'data' in body
        ) {
            return body.data as T;
        }
        return body as T;
    }

    async get<T>(path: string, params?: Record<string, any>): Promise<T> {
        try {
            const response = await this.client.get<any>(path, { params });
            return this.unwrapBody<T>(response.data);
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async post<T>(path: string, body?: any): Promise<T> {
        try {
            const response = await this.client.post<any>(path, body);
            return this.unwrapBody<T>(response.data);
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async put<T>(path: string, body?: any): Promise<T> {
        try {
            const response = await this.client.put<any>(path, body);
            return this.unwrapBody<T>(response.data);
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async patch<T>(path: string, body?: any): Promise<T> {
        try {
            const response = await this.client.patch<any>(path, body);
            return this.unwrapBody<T>(response.data);
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async delete<T = void>(path: string): Promise<T> {
        try {
            const response = await this.client.delete<any>(path);
            return this.unwrapBody<T>(response.data);
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Fetch a raw file (e.g. a server-generated CSV export) as a Blob, with
     * the same auth header as every other request. Callers turn the Blob
     * into a download via `triggerBlobDownload` (src/utils/downloadBlob.ts).
     */
    async downloadBlob(path: string, params?: Record<string, any>): Promise<Blob> {
        try {
            const response = await this.client.get<Blob>(path, { params, responseType: "blob" });
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Handle API errors. Backend returns errors as
     *   { error: { code, message, details? } }
     * but legacy endpoints sometimes return { code, message, ... } directly.
     */
    private handleError(error: any): Error {
        if (axios.isAxiosError(error)) {
            const body = error.response?.data;
            const apiError =
                body?.error && typeof body.error === 'object'
                    ? (body.error as ApiError)
                    : (body as ApiError | undefined);
            if (apiError?.message) {
                return new Error(apiError.message);
            }
            if (error.response?.status === 401) {
                return new Error("Unauthorized - please login again");
            }
            if (error.response?.status === 403) {
                return new Error("You do not have permission to perform this action");
            }
            if (error.response?.status === 404) {
                return new Error("Resource not found");
            }
            if (error.response?.status === 422) {
                return new Error("Invalid input - please check your data");
            }
            return new Error(error.message || "An error occurred");
        }
        return error instanceof Error ? error : new Error("An unknown error occurred");
    }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;
